import { useState } from 'react';
import { isNil } from 'es-toolkit';

import type { AccountGroupId } from '../constants';
import type { AccountRowOrder } from '../row-order';
import { moveRowBy, moveRowTo } from '../row-order';

export const ACCOUNT_ROW_ORDER_STORAGE_PREFIX = 'client-portal-accounts-order';

export function accountRowOrderStorageKey(scopeId: string) {
  return `${ACCOUNT_ROW_ORDER_STORAGE_PREFIX}:${scopeId}`;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function parseStoredOrder(raw: string | null): AccountRowOrder {
  if (isNil(raw)) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || isNil(parsed)) {
      return {};
    }
    const entries = Object.entries(parsed).filter(([, ids]) => isStringArray(ids));
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
}

function readStoredOrder(scopeId: string | null): AccountRowOrder {
  if (typeof window === 'undefined' || isNil(scopeId)) {
    return {};
  }
  try {
    return parseStoredOrder(window.localStorage.getItem(accountRowOrderStorageKey(scopeId)));
  } catch {
    return {};
  }
}

function persistOrder(scopeId: string | null, order: AccountRowOrder) {
  if (isNil(scopeId)) {
    return;
  }
  try {
    window.localStorage.setItem(accountRowOrderStorageKey(scopeId), JSON.stringify(order));
  } catch {
    return;
  }
}

export type AccountRowOrderState = {
  readonly order: AccountRowOrder;
  readonly dropRowOnto: (groupId: AccountGroupId, rowIds: readonly string[], movedId: string, targetId: string) => void;
  readonly nudgeRow: (groupId: AccountGroupId, rowIds: readonly string[], movedId: string, offset: number) => void;
};

export function useAccountRowOrder(scopeId: string | null): AccountRowOrderState {
  const [order, setOrder] = useState<AccountRowOrder>(() => readStoredOrder(scopeId));
  const [loadedScopeId, setLoadedScopeId] = useState(scopeId);

  if (loadedScopeId !== scopeId) {
    setLoadedScopeId(scopeId);
    setOrder(readStoredOrder(scopeId));
  }

  function store(groupId: AccountGroupId, rowIds: readonly string[]) {
    const next: AccountRowOrder = { ...order, [groupId]: rowIds };
    setOrder(next);
    persistOrder(scopeId, next);
  }

  return {
    order,
    dropRowOnto: (groupId, rowIds, movedId, targetId) => {
      store(groupId, moveRowTo(rowIds, movedId, targetId));
    },
    nudgeRow: (groupId, rowIds, movedId, offset) => {
      store(groupId, moveRowBy(rowIds, movedId, offset));
    },
  };
}
